(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var DESKTOP = window.matchMedia(
    (window.theme && window.theme.mediaQueries && window.theme.mediaQueries.md) || '(min-width: 768px)'
  );

  function parseJSON(text, fallback) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return fallback;
    }
  }

  function readStorage(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
    }
  }

  function fill(template, map) {
    return String(template || '').replace(/\{(\w+)\}/g, function (match, token) {
      return Object.prototype.hasOwnProperty.call(map, token) ? map[token] : match;
    });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Reads the numbers out of a size value so sizes can be compared however they are
  // written: "50cm", "50 cm", "45-50 cm", "Chain Set 51 + 52 + 53 cm", 18".
  // kind: 'range' (two numbers joined by a dash), 'set' (several numbers, e.g. a chain
  // set) or 'single'. Returns null when the value has no number at all.
  function parseSize(value) {
    var text = String(value || '');
    var numbers = (text.match(/\d+(?:\.\d+)?/g) || []).map(parseFloat);
    if (!numbers.length) return null;
    var kind = 'single';
    if (numbers.length === 2 && /\d\s*[-–—]\s*\d/.test(text)) kind = 'range';
    else if (numbers.length > 1) kind = 'set';
    return { min: Math.min.apply(null, numbers), max: Math.max.apply(null, numbers), kind: kind };
  }

  function afterTransition(el, callback, fallbackMs) {
    if (REDUCED_MOTION.matches) {
      callback();
      return;
    }
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      callback();
    };
    var onEnd = function (evt) {
      if (evt.target === el) finish();
    };
    el.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, fallbackMs || 450);
  }

  function nextFrame(callback) {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(callback);
    });
  }

  function trap(container, focusEl) {
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(container, focusEl || container);
    } else if (focusEl || container) {
      (focusEl || container).focus();
    }
  }

  function untrap(returnTo) {
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(returnTo || null);
    } else if (returnTo && typeof returnTo.focus === 'function') {
      returnTo.focus();
    }
  }

  if (!customElements.get('bys-card')) {
    customElements.define(
      'bys-card',
      class BysCard extends HTMLElement {
        connectedCallback() {
          if (this.bound) return;
          this.bound = true;
          this.onClick = this.onClick.bind(this);
          this.addEventListener('click', this.onClick);
        }

        panels() {
          return Array.prototype.slice.call(this.querySelectorAll('[data-bys-panel]'));
        }

        activePanel() {
          return this.querySelector('[data-bys-panel]:not([hidden])');
        }

        showPanel(productId, silent) {
          var id = String(productId);
          var found = false;
          this.panels().forEach(function (panel) {
            var match = panel.dataset.bysProduct === id;
            panel.hidden = !match;
            if (match) found = true;
          });
          if (!found) return;
          this.activeId = id;
          this.querySelectorAll('[data-bys-switch]').forEach(function (swatch) {
            swatch.setAttribute('aria-pressed', swatch.dataset.bysSwitch === id ? 'true' : 'false');
          });
          if (!silent) {
            this.dispatchEvent(new CustomEvent('bys:switch', { bubbles: true, detail: { productId: id } }));
          }
        }

        // Called by the stack after panels of this colour group arrive from a batch:
        // orders the panels, rebuilds the swatch row in each of them and picks the
        // panel to show unless the shopper already chose one.
        syncGroup(currentProductId, currentColor) {
          var self = this;
          var panels = this.panels();
          if (!panels.length) return;
          panels.sort(function (a, b) {
            return (parseInt(a.dataset.bysOrder, 10) || 0) - (parseInt(b.dataset.bysOrder, 10) || 0);
          });
          panels.forEach(function (panel) { self.appendChild(panel); });

          var grouped = panels.length > 1;
          panels.forEach(function (panel) {
            var row = panel.querySelector('[data-bys-swatches]');
            if (!row) return;
            while (row.firstChild) row.removeChild(row.firstChild);
            row.hidden = !grouped;
            if (!grouped) return;
            panels.forEach(function (other) {
              var swatch = document.createElement('button');
              swatch.type = 'button';
              swatch.className = 'bys-card__swatch';
              swatch.dataset.bysSwitch = other.dataset.bysProduct;
              swatch.style.setProperty('--bys-swatch', other.dataset.bysSwatch || '#CCCCCC');
              swatch.setAttribute('aria-label', other.dataset.bysSwatchLabel || other.dataset.bysTitle || '');
              swatch.setAttribute('aria-pressed', other === panel ? 'true' : 'false');
              row.appendChild(swatch);
            });
          });

          if (this.userPicked && this.activeId) {
            this.showPanel(this.activeId, true);
            return;
          }
          var pick = null;
          panels.forEach(function (panel) {
            if (!pick && panel.dataset.bysProduct === String(currentProductId)) pick = panel;
          });
          if (!pick && currentColor) {
            panels.forEach(function (panel) {
              if (!pick && (panel.dataset.bysColor || '').toLowerCase() === currentColor) pick = panel;
            });
          }
          this.showPanel((pick || panels[0]).dataset.bysProduct, true);
        }

        setVariant(panel, variantId, silent) {
          var chips = panel.querySelectorAll('[data-bys-chip]');
          var chip = null;
          chips.forEach(function (c) {
            if (c.dataset.bysVariant === String(variantId)) chip = c;
          });
          if (!chip || chip.disabled) return false;
          chips.forEach(function (c) {
            var active = c === chip;
            c.classList.toggle('bys-card__chip--active', active);
            c.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          panel.dataset.bysVariant = chip.dataset.bysVariant;
          panel.dataset.bysPrice = chip.dataset.bysPrice;
          panel.dataset.bysPriceHtml = chip.dataset.bysPriceHtml;
          panel.dataset.bysSaleHtml = chip.dataset.bysSaleHtml;
          var was = panel.querySelector('[data-bys-was]');
          var now = panel.querySelector('[data-bys-now]');
          if (was) was.innerHTML = chip.dataset.bysPriceHtml;
          if (now) now.innerHTML = chip.dataset.bysSaleHtml;
          if (!silent) {
            this.dispatchEvent(new CustomEvent('bys:variant', {
              bubbles: true,
              detail: { productId: panel.dataset.bysProduct, variantId: panel.dataset.bysVariant }
            }));
          }
          return true;
        }

        page(panel, direction) {
          var shots = panel.querySelectorAll('[data-bys-shot]');
          var count = shots.length;
          if (count < 2) return;
          var current = parseInt(panel.dataset.bysShotIndex || '0', 10) || 0;
          var next = (((current + direction) % count) + count) % count;
          this.fillShot(panel, shots[next], next);
          shots.forEach(function (shot, index) {
            shot.classList.toggle('bys-card__shot--active', index === next);
          });
          panel.dataset.bysShotIndex = String(next);
        }

        fillShot(panel, shot, index) {
          if (!shot || shot.querySelector('img')) return;
          if (!panel.bysImages) panel.bysImages = parseJSON(panel.dataset.bysImages, []);
          var position = parseInt(shot.dataset.bysShotIndex || String(index), 10) || index;
          var data = Array.isArray(panel.bysImages) ? panel.bysImages[position] : null;
          if (!data || !(data.card || data.src)) return;
          var img = document.createElement('img');
          img.className = 'bys-card__img';
          img.src = data.card || data.src;
          img.alt = data.alt || '';
          img.width = 600;
          img.height = 600;
          img.decoding = 'async';
          shot.appendChild(img);
        }

        onClick(evt) {
          var panel = evt.target.closest('[data-bys-panel]');
          if (!panel || panel.hidden || !this.contains(panel)) return;
          var target = evt.target;
          var fixed = panel.hasAttribute('data-bys-fixed');

          if (target.closest('[data-bys-prev]')) {
            this.page(panel, -1);
            return;
          }
          if (target.closest('[data-bys-next]')) {
            this.page(panel, 1);
            return;
          }

          var zoomBtn = target.closest('[data-bys-zoom]');
          if (zoomBtn) {
            var images = parseJSON(panel.dataset.bysImages, []);
            if (!Array.isArray(images) || images.length === 0) return;
            this.dispatchEvent(new CustomEvent('bys:zoom', {
              bubbles: true,
              detail: {
                title: panel.dataset.bysTitle || '',
                images: images,
                index: parseInt(panel.dataset.bysShotIndex || '0', 10) || 0,
                opener: zoomBtn
              }
            }));
            return;
          }

          var chip = target.closest('[data-bys-chip]');
          if (chip) {
            if (chip.disabled || fixed) return;
            this.setVariant(panel, chip.dataset.bysVariant, false);
            return;
          }

          var swatch = target.closest('[data-bys-switch]');
          if (swatch) {
            this.userPicked = true;
            this.showPanel(swatch.dataset.bysSwitch, false);
            return;
          }

          if (target.closest('a')) return;
          if (fixed) return;

          this.dispatchEvent(new CustomEvent('bys:toggle', {
            bubbles: true,
            detail: { productId: panel.dataset.bysProduct, variantId: panel.dataset.bysVariant, panel: panel }
          }));
        }
      }
    );
  }

  if (!customElements.get('bys-zoom')) {
    customElements.define(
      'bys-zoom',
      class BysZoom extends HTMLElement {
        connectedCallback() {
          if (this.bound) return;
          this.bound = true;
          this.isOpen = false;
          this.panel = this.querySelector('.bys-zoom__panel');
          this.titleEl = this.querySelector('[data-bys-zoom-title]');
          this.mainEl = this.querySelector('[data-bys-zoom-main]');
          this.slidesEl = this.querySelector('[data-bys-zoom-slides]');
          this.thumbsEl = this.querySelector('[data-bys-zoom-thumbs]');
          this.thumbSlidesEl = this.querySelector('[data-bys-zoom-thumb-slides]');
          this.prevBtn = this.querySelector('[data-bys-zoom-prev]');
          this.nextBtn = this.querySelector('[data-bys-zoom-next]');

          this.onClick = this.onClick.bind(this);
          this.addEventListener('click', this.onClick);
        }

        disconnectedCallback() {
          this.removeAttribute('open');
          this.destroySliders();
        }

        onClick(evt) {
          if (evt.target.closest('[data-bys-zoom-close]')) {
            evt.preventDefault();
            this.close();
          }
        }

        open(title, images, startIndex, opener) {
          if (this.isOpen || !this.panel || !images || !images.length) return;
          this.opener = opener || null;
          this.isOpen = true;

          if (this.titleEl) this.titleEl.textContent = title || '';
          this.slidesEl.innerHTML = images.map(function (img) {
            return '<div class="swiper-slide"><img src="' + escapeHtml(img.src) + '" alt="' + escapeHtml(img.alt || title) + '" width="1200" height="1200" loading="lazy"></div>';
          }).join('');
          this.thumbSlidesEl.innerHTML = images.map(function (img) {
            return '<div class="swiper-slide"><img src="' + escapeHtml(img.thumb || img.src) + '" alt="" width="70" height="105" loading="lazy"></div>';
          }).join('');

          this.hidden = false;
          this.setAttribute('open', '');

          var self = this;
          nextFrame(function () {
            self.classList.add('is-open');
            self.initSliders(Math.min(Math.max(startIndex || 0, 0), images.length - 1));
            trap(self, self.panel);
          });
        }

        close() {
          if (!this.isOpen) return;
          this.isOpen = false;
          this.removeAttribute('open');
          this.classList.remove('is-open');
          var self = this;
          afterTransition(this.panel, function () {
            self.hidden = true;
            self.destroySliders();
            untrap(self.opener);
            self.dispatchEvent(new CustomEvent('bys:zoom-closed', { bubbles: true }));
          }, 350);
        }

        initSliders(startIndex) {
          var Swiper = window.Swiper;
          if (typeof Swiper !== 'function') {
            this.initFallback(startIndex);
            return;
          }
          this.mainEl.classList.remove('bys-zoom__main--fallback');
          this.thumbsEl.classList.remove('bys-zoom__thumbs--fallback');

          this.thumbs = new Swiper(this.thumbsEl, {
            slidesPerView: 'auto',
            spaceBetween: 10,
            watchSlidesProgress: true,
            freeMode: true,
            slideToClickedSlide: true
          });
          this.main = new Swiper(this.mainEl, {
            initialSlide: startIndex,
            loop: false,
            spaceBetween: 10,
            navigation: { prevEl: this.prevBtn, nextEl: this.nextBtn },
            thumbs: { swiper: this.thumbs },
            keyboard: { enabled: true }
          });
        }

        initFallback(startIndex) {
          var self = this;
          this.mainEl.classList.add('bys-zoom__main--fallback');
          this.thumbsEl.classList.add('bys-zoom__thumbs--fallback');
          var track = this.slidesEl;
          var slides = track.children;
          var index = startIndex;

          var go = function (next) {
            index = Math.min(Math.max(next, 0), slides.length - 1);
            track.scrollTo({ left: track.clientWidth * index, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
            self.prevBtn.disabled = index === 0;
            self.nextBtn.disabled = index === slides.length - 1;
            Array.prototype.forEach.call(self.thumbSlidesEl.children, function (thumb, i) {
              thumb.classList.toggle('swiper-slide-thumb-active', i === index);
            });
          };
          this.fallbackPrev = function () { go(index - 1); };
          this.fallbackNext = function () { go(index + 1); };
          this.fallbackThumb = function (evt) {
            var slide = evt.target.closest('.swiper-slide');
            if (!slide) return;
            go(Array.prototype.indexOf.call(self.thumbSlidesEl.children, slide));
          };
          this.prevBtn.addEventListener('click', this.fallbackPrev);
          this.nextBtn.addEventListener('click', this.fallbackNext);
          this.thumbSlidesEl.addEventListener('click', this.fallbackThumb);
          go(startIndex);
        }

        destroySliders() {
          if (this.main && this.main.destroy) this.main.destroy(true, true);
          if (this.thumbs && this.thumbs.destroy) this.thumbs.destroy(true, true);
          this.main = null;
          this.thumbs = null;
          if (this.fallbackPrev) {
            this.prevBtn.removeEventListener('click', this.fallbackPrev);
            this.nextBtn.removeEventListener('click', this.fallbackNext);
            this.thumbSlidesEl.removeEventListener('click', this.fallbackThumb);
            this.fallbackPrev = this.fallbackNext = this.fallbackThumb = null;
            this.prevBtn.disabled = false;
            this.nextBtn.disabled = false;
          }
          if (this.slidesEl) this.slidesEl.innerHTML = '';
          if (this.thumbSlidesEl) this.thumbSlidesEl.innerHTML = '';
        }
      }
    );
  }

  if (!customElements.get('bys-stack')) {
    customElements.define(
      'bys-stack',
      class BysStack extends HTMLElement {
        connectedCallback() {
          this.total = Math.min(Math.max(parseInt(this.dataset.bysTotal, 10) || 3, 2), 5);
          this.percent = parseInt(this.dataset.bysPercent, 10);
          if (isNaN(this.percent)) this.percent = 50;
          this.productId = String(this.dataset.bysProductId || '');
          this.storageKey = 'bys:' + (this.dataset.bysKey || this.productId);
          this.designMode = this.hasAttribute('data-bys-design-mode');
          this.formId = this.dataset.bysFormId || '';
          this.labels = {
            tray: this.dataset.bysTrayText || '{count} of {total}',
            emptyTitle: this.dataset.bysEmptyTitle || '',
            emptyText: this.dataset.bysEmptyText || '',
            selectSize: this.dataset.bysSelectSize || '',
            add: this.dataset.bysAddLabel || '',
            remove: this.dataset.bysRemoveLabel || '',
            replacePiece: this.dataset.bysReplaceLabel || '',
            removePiece: this.dataset.bysRemovePieceLabel || '',
            addError: this.dataset.bysAddError || '',
            inStack: this.dataset.bysInStackLabel || ''
          };
          this.currentColor = (this.dataset.bysCurrentColor || '').toLowerCase();

          this.rowEl = this.querySelector('[data-bys-row]');
          this.startBtn = this.querySelector('[data-bys-start]');
          this.collapseBtn = this.querySelector('[data-bys-collapse]');
          this.productRoot = this.closest('.js-product');
          this.stage = this.querySelector('[data-bys-stage]');
          this.slotsEl = this.querySelector('[data-bys-slots]');
          this.mainSlot = this.querySelector('[data-bys-main-slot]');
          this.mainPriceEl = this.querySelector('[data-bys-main-price]');
          this.templates = {
            empty: this.querySelector('[data-bys-tpl="empty"]'),
            filled: this.querySelector('[data-bys-tpl="filled"]')
          };
          var mainJson = this.querySelector('[data-bys-main]');
          this.mainData = (mainJson && parseJSON(mainJson.textContent, null)) || { title: '', image: '', variants: {} };
          var sourcesJson = this.querySelector('[data-bys-sources]');
          this.sources = (sourcesJson && parseJSON(sourcesJson.textContent, null)) || [];
          if (!Array.isArray(this.sources)) this.sources = [];
          this.zoomIconTpl = this.querySelector('template[data-bys-tpl="zoom-icon"]');
          this.modal = this.querySelector('[data-bys-modal]');
          this.zoom = this.querySelector('[data-bys-zoom-modal]');

          if (!this.rowEl || !this.stage || !this.slotsEl || !this.modal || !this.templates.empty || !this.templates.filled) return;

          this.panel = this.modal.querySelector('[data-bys-panel]');
          this.grid = this.modal.querySelector('[data-bys-grid]');
          this.emptyMsg = this.modal.querySelector('[data-bys-empty]');
          this.filtersEl = this.modal.querySelector('[data-bys-filters]');
          this.resetBtn = this.modal.querySelector('[data-bys-reset]');
          this.tray = this.modal.querySelector('[data-bys-tray]');
          this.trayToggle = this.modal.querySelector('[data-bys-tray-toggle]');
          this.traySlotsEl = this.modal.querySelector('[data-bys-tray-slots]');
          this.counterEl = this.modal.querySelector('[data-bys-counter]');
          this.loadingEl = this.modal.querySelector('[data-bys-loading]');
          this.loadErrorEl = this.modal.querySelector('[data-bys-load-error]');
          this.body = this.modal.querySelector('[data-bys-body]');

          // Grid loading, one batch at a time: 'idle' → 'loading' (batch in flight) → 'ready'
          // (more to load when the end of the list nears the view) → 'loaded' | 'error' (retry available)
          this.gridState = 'idle';
          this.batchesLoaded = 0;
          this.scrollFrame = null;
          this.pendingItems = null;
          this.slotImages = {};
          this.panelIndex = {};
          this.cardIndex = {};
          this.sizeValues = [];
          this.sizeGroups = {};
          this.queue = [];
          this.failedJobs = [];
          this.inFlight = 0;
          this.abortController = null;

          this.form = this.formId ? document.getElementById(this.formId) : null;
          this.stickyForm = this.formId ? document.getElementById(this.formId + '-sticky') : null;
          var idInput = this.form ? this.form.querySelector('[name="id"]') : null;

          this.items = [{ productId: this.productId, variantId: idInput && idInput.value ? String(idInput.value) : null }];
          this.expanded = false;
          this.replaceIndex = null;
          this.filters = { type: '', color: '', size: '' };
          this.isOpen = false;
          this.opener = null;

          this.modal.bysStack = this;
          document.body.appendChild(this.modal);
          if (this.zoom) document.body.appendChild(this.zoom);

          this.bind();
          this.readStored();
          this.syncTrayMode();
          this.renderAll();
        }

        disconnectedCallback() {
          window.clearTimeout(this.collapseTimer);
          window.cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = null;
          this.unbind();
          if (this.abortController) this.abortController.abort();
          this.abortController = null;
          this.queue = [];
          this.inFlight = 0;
          if (this.gridState === 'loading') this.gridState = 'idle';
          if (this.modal) {
            this.modal.removeAttribute('open');
            this.modal.hidden = true;
          }
          if (this.zoom) {
            this.zoom.removeAttribute('open');
            this.zoom.hidden = true;
          }
          if (this.modal && this.modal.parentNode !== this) this.appendChild(this.modal);
          if (this.zoom && this.zoom.parentNode !== this) this.appendChild(this.zoom);
          if (this.productRoot) this.productRoot.classList.remove('bys-has-extras');
        }

        bind() {
          this.onBlockClick = this.onBlockClick.bind(this);
          this.onModalClick = this.onModalClick.bind(this);
          this.onToggle = this.onToggle.bind(this);
          this.onVariantPick = this.onVariantPick.bind(this);
          this.onSwitch = this.onSwitch.bind(this);
          this.onZoom = this.onZoom.bind(this);
          this.onZoomClosed = this.onZoomClosed.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.onSubmit = this.onSubmit.bind(this);
          this.onVariantChange = this.onVariantChange.bind(this);
          this.onResize = this.onResize.bind(this);
          this.onBodyScroll = this.onBodyScroll.bind(this);
          this.onDesktopChange = this.syncTrayMode.bind(this);

          this.addEventListener('click', this.onBlockClick);
          this.modal.addEventListener('click', this.onModalClick);
          if (this.body) this.body.addEventListener('scroll', this.onBodyScroll, { passive: true });
          this.modal.addEventListener('bys:toggle', this.onToggle);
          this.modal.addEventListener('bys:variant', this.onVariantPick);
          this.modal.addEventListener('bys:switch', this.onSwitch);
          this.modal.addEventListener('bys:zoom', this.onZoom);
          if (this.zoom) this.zoom.addEventListener('bys:zoom-closed', this.onZoomClosed);
          document.addEventListener('submit', this.onSubmit, true);
          document.addEventListener('on:variant:change', this.onVariantChange);
          window.addEventListener('on:debounced-resize', this.onResize);
          if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', this.onDesktopChange);
        }

        unbind() {
          this.removeEventListener('click', this.onBlockClick);
          if (this.modal) {
            this.modal.removeEventListener('click', this.onModalClick);
            this.modal.removeEventListener('bys:toggle', this.onToggle);
            this.modal.removeEventListener('bys:variant', this.onVariantPick);
            this.modal.removeEventListener('bys:switch', this.onSwitch);
            this.modal.removeEventListener('bys:zoom', this.onZoom);
          }
          if (this.body) this.body.removeEventListener('scroll', this.onBodyScroll);
          if (this.zoom) this.zoom.removeEventListener('bys:zoom-closed', this.onZoomClosed);
          document.removeEventListener('submit', this.onSubmit, true);
          document.removeEventListener('on:variant:change', this.onVariantChange);
          document.removeEventListener('keydown', this.onKeydown);
          window.removeEventListener('on:debounced-resize', this.onResize);
          if (DESKTOP.removeEventListener && this.onDesktopChange) DESKTOP.removeEventListener('change', this.onDesktopChange);
        }

        readStored() {
          if (this.designMode) return;
          var stored = readStorage(this.storageKey);
          if (!stored || typeof stored !== 'object') return;
          this.expanded = !!stored.expanded;
          this.pendingItems = Array.isArray(stored.items) ? stored.items : [];
          var self = this;
          // Entries saved with display data fill their slots right away. Their card
          // confirms them (or drops them) once its batch has loaded.
          this.pendingItems.forEach(function (entry) {
            if (!entry || !entry.productId || !entry.display) return;
            var productId = String(entry.productId);
            if (productId === self.productId) return;
            if (self.items.length >= self.total) return;
            if (self.items.some(function (i) { return i.productId === productId; })) return;
            self.items.push({
              productId: productId,
              variantId: entry.variantId ? String(entry.variantId) : null,
              display: entry.display,
              pending: true
            });
          });
        }

        // Matches stored stack entries against the cards loaded so far. Entries whose
        // card hasn't arrived yet stay pending until loading finishes.
        restoreItems() {
          var self = this;
          var remaining = [];
          (this.pendingItems || []).forEach(function (entry) {
            if (!entry || !entry.productId) return;
            var productId = String(entry.productId);
            if (productId === self.productId) return;
            var panel = self.panelFor(productId);
            if (!panel) {
              remaining.push(entry);
              return;
            }
            var variantId = entry.variantId ? String(entry.variantId) : null;
            var card = panel.closest('bys-card');
            if (variantId && card && panel.querySelector('[data-bys-chip]')) {
              if (!card.setVariant(panel, variantId, true)) variantId = panel.dataset.bysVariant;
            } else {
              variantId = panel.dataset.bysVariant;
            }
            var existing = null;
            for (var i = 1; i < self.items.length; i += 1) {
              if (self.items[i].productId === productId) existing = self.items[i];
            }
            if (existing) {
              existing.variantId = variantId;
              delete existing.display;
              delete existing.pending;
            } else if (self.items.length < self.total) {
              self.items.push({ productId: productId, variantId: variantId });
            } else {
              return;
            }
            if (card && typeof card.showPanel === 'function' && !card.querySelector('[data-bys-panel][data-bys-fixed]:not([hidden])')) {
              card.userPicked = true;
              card.showPanel(productId, true);
            }
          });
          this.pendingItems = remaining.length ? remaining : null;
        }

        // Once every batch is in, stored items whose product is no longer offered are dropped.
        dropUnconfirmed() {
          var before = this.items.length;
          this.items = this.items.filter(function (item, index) { return index === 0 || !item.pending; });
          if (this.items.length !== before) this.replaceIndex = null;
          this.pendingItems = null;
        }

        forgetPending(productId) {
          if (!this.pendingItems) return;
          var id = String(productId);
          this.pendingItems = this.pendingItems.filter(function (entry) { return entry && String(entry.productId) !== id; });
          if (!this.pendingItems.length) this.pendingItems = null;
        }

        persist() {
          if (this.designMode) return;
          var self = this;
          var extras = this.items.slice(1).map(function (item, index) {
            var data = self.dataFor(item, index + 1);
            return {
              productId: item.productId,
              variantId: item.variantId,
              display: data
                ? { title: data.title, image: data.image, price: data.price, priceHtml: data.priceHtml, saleHtml: data.saleHtml }
                : item.display || null
            };
          });
          if (this.gridState !== 'loaded' && this.pendingItems) {
            this.pendingItems.forEach(function (entry) {
              if (!entry || !entry.productId) return;
              if (extras.some(function (e) { return e.productId === String(entry.productId); })) return;
              extras.push(entry);
            });
          }
          writeStorage(this.storageKey, { expanded: this.expanded, items: extras });
        }

        // Loads the popup cards from sections/bys-grid.liquid one batch at a time: the first
        // when the popup opens, the next whenever the end of the list nears the view.
        // Called on open and to retry.
        loadGrid() {
          if (!this.grid || this.inFlight || this.gridState === 'loaded') return;
          var self = this;
          if (this.gridState === 'idle') {
            this.queue = [];
            this.failedJobs = [];
            this.sources.forEach(function (source, index) {
              if (!source || !source.url) return;
              source.index = index;
              source.pages = Math.max(parseInt(source.pages, 10) || 1, 1);
              source.queued = 0;
              self.queueSource(source, false);
            });
          } else if (this.gridState === 'error') {
            this.queue = this.failedJobs.concat(this.queue);
            this.failedJobs = [];
            this.gridState = 'ready';
            if (this.loadErrorEl) this.loadErrorEl.hidden = true;
          }
          if (!this.abortController && window.AbortController) this.abortController = new AbortController();
          this.pump();
        }

        queueSource(source, front) {
          var join = source.url.indexOf('?') === -1 ? '?' : '&';
          var jobs = [];
          while (source.queued < source.pages) {
            source.queued += 1;
            jobs.push({ source: source, page: source.queued, url: source.url + join + 'page=' + source.queued });
          }
          if (!front) {
            this.queue = this.queue.concat(jobs);
            return;
          }
          var at = 0;
          this.queue.forEach(function (job, index) {
            if (job.source === source) at = index + 1;
          });
          Array.prototype.splice.apply(this.queue, [at, 0].concat(jobs));
        }

        pump() {
          if (this.inFlight || this.gridState === 'error' || this.gridState === 'loaded') return;
          if (!this.queue.length) {
            this.finishLoading();
            return;
          }
          if (!this.needsMore()) {
            this.gridState = 'ready';
            return;
          }
          this.gridState = 'loading';
          if (this.loadingEl) this.loadingEl.hidden = false;
          this.fetchBatch(this.queue.shift());
        }

        needsMore() {
          if (!this.batchesLoaded) return true;
          if (!this.isOpen || !this.body) return false;
          var view = this.body.getBoundingClientRect();
          return this.grid.getBoundingClientRect().bottom - view.bottom < view.height * 2;
        }

        fetchBatch(job) {
          var self = this;
          this.inFlight += 1;
          var options = { headers: { Accept: 'text/html' } };
          if (this.abortController) options.signal = this.abortController.signal;
          fetch(job.url, options)
            .then(function (response) {
              if (!response.ok) throw new Error(String(response.status));
              return response.text();
            })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var batch = doc.querySelector('[data-bys-batch]');
              if (!batch) {
                var liquidError = /Liquid (?:syntax )?error[^<]*/i.exec(html);
                throw new Error(liquidError ? liquidError[0].trim() : 'No batch in response (' + html.length + ' chars)');
              }
              self.importBatch(batch, job);
            })
            .catch(function (error) {
              if (error && error.name === 'AbortError') return;
              job.error = error && error.message ? error.message : String(error);
              if (window.console && console.warn) console.warn('[build-your-stack] batch failed: ' + job.error + ' — ' + job.url);
              self.failedJobs.push(job);
            })
            .then(function () {
              self.inFlight = Math.max(0, self.inFlight - 1);
              if (self.gridState !== 'loading') return;
              if (self.loadingEl) self.loadingEl.hidden = true;
              if (self.failedJobs.length) {
                self.showLoadError();
                return;
              }
              self.gridState = 'ready';
              self.pump();
            });
        }

        importBatch(batch, job) {
          var source = job.source;
          var pages = parseInt(batch.dataset.bysPages, 10);
          if (pages > source.pages) {
            source.pages = pages;
            this.queueSource(source, true);
          } else if (pages > 0 && pages < source.pages) {
            source.pages = pages;
            this.queue = this.queue.filter(function (queued) { return queued.source !== source || queued.page <= pages; });
          }
          var anchor = this.scrollAnchor();
          this.batchesLoaded += 1;
          this.mergeOptions(batch);
          this.mergeCards(batch, job);
          this.restoreItems();
          this.applyFilters(true);
          this.persist();
          this.renderAll();
          this.warmImages();
          this.keepAnchor(anchor);
        }

        scrollAnchor() {
          if (!this.isOpen || !this.body || !this.body.scrollTop) return null;
          var top = this.body.getBoundingClientRect().top;
          var cards = this.grid.children;
          for (var i = 0; i < cards.length; i += 1) {
            if (cards[i].hidden) continue;
            var rect = cards[i].getBoundingClientRect();
            if (rect.bottom > top) return { el: cards[i], top: rect.top };
          }
          return null;
        }

        keepAnchor(anchor) {
          if (!anchor || !anchor.el.isConnected || anchor.el.hidden) return;
          var shift = anchor.el.getBoundingClientRect().top - anchor.top;
          if (shift) this.body.scrollTop += shift;
        }

        markSwatchRows() {
          if (!this.isOpen || !this.grid) return;
          var cards = Array.prototype.filter.call(this.grid.children, function (card) { return !card.hidden; });
          var columns = getComputedStyle(this.grid).gridTemplateColumns.split(' ').length || 1;
          for (var start = 0; start < cards.length; start += columns) {
            var row = cards.slice(start, start + columns);
            var swatches = row.map(function (card) { return !!card.querySelector('[data-bys-swatches]:not([hidden])'); });
            var rowHasSwatches = swatches.indexOf(true) !== -1;
            row.forEach(function (card, index) {
              card.classList.toggle('bys-card--swatch-space', rowHasSwatches && !swatches[index]);
            });
          }
        }

        finishLoading() {
          this.abortController = null;
          if (this.loadingEl) this.loadingEl.hidden = true;
          this.gridState = 'loaded';
          this.dropUnconfirmed();
          this.applyFilters(true);
          this.persist();
          this.renderAll();
        }

        showLoadError() {
          this.gridState = 'error';
          if (this.loadingEl) this.loadingEl.hidden = true;
          if (this.loadErrorEl) {
            this.loadErrorEl.hidden = false;
            this.showDiagnostics();
          }
          this.applyFilters(true);
          this.persist();
          this.renderAll();
        }

        onBodyScroll() {
          if (this.gridState !== 'ready' || this.scrollFrame) return;
          var self = this;
          this.scrollFrame = window.requestAnimationFrame(function () {
            self.scrollFrame = null;
            if (self.gridState === 'ready') self.pump();
          });
        }

        // Theme editor only: lists why batches failed under the error message.
        showDiagnostics() {
          if (!this.designMode || !this.loadErrorEl) return;
          var line = this.loadErrorEl.querySelector('[data-bys-diagnostics]');
          if (!line) {
            line = document.createElement('small');
            line.setAttribute('data-bys-diagnostics', '');
            line.className = 'bys-modal__diagnostics';
            this.loadErrorEl.appendChild(line);
          }
          line.textContent = this.failedJobs.slice(0, 3).map(function (job) {
            return (job.error || 'failed') + ' — ' + job.url;
          }).join(' | ');
        }

        // Adds the filter options found in a batch to the filter lists in the modal:
        // types come rendered by Liquid (they need collection titles), colours and
        // sizes come from the cards' data attributes.
        mergeOptions(batch) {
          var self = this;
          if (!this.filtersEl) return;
          var found = { type: [], color: [] };
          var sizeAdded = false;
          Array.prototype.forEach.call(batch.querySelectorAll('[data-bys-options="type"] .bys-filter__option'), function (button) {
            if (button.dataset.bysValue) found.type.push({ value: button.dataset.bysValue, label: button.textContent.trim() });
          });
          Array.prototype.forEach.call(batch.querySelectorAll('[data-bys-panel]'), function (panel) {
            var color = panel.dataset.bysColor;
            if (color) found.color.push({ value: color, label: color });
            (panel.dataset.bysSizes || '').split('|').forEach(function (size) {
              if (!size || self.sizeValues.indexOf(size) !== -1) return;
              self.sizeValues.push(size);
              sizeAdded = true;
            });
          });
          Object.keys(found).forEach(function (name) {
            var filter = self.filtersEl.querySelector('[data-bys-filter="' + name + '"]');
            var listEl = filter ? filter.querySelector('.bys-filter__panel') : null;
            if (!listEl) return;
            var known = {};
            listEl.querySelectorAll('.bys-filter__option').forEach(function (option) {
              known[option.dataset.bysValue || ''] = true;
            });
            found[name].forEach(function (entry) {
              if (known[entry.value]) return;
              known[entry.value] = true;
              listEl.appendChild(self.filterOption(entry.value, entry.label, false));
            });
            filter.hidden = Object.keys(known).length < 2;
          });
          if (sizeAdded) this.rebuildSizeOptions();
          this.fitFilters();
        }

        filterOption(value, label, selected) {
          var li = document.createElement('li');
          li.setAttribute('role', 'none');
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'bys-filter__option' + (selected ? ' bys-filter__option--active' : '');
          button.setAttribute('role', 'option');
          button.setAttribute('aria-selected', selected ? 'true' : 'false');
          button.dataset.bysValue = value;
          button.textContent = label;
          li.appendChild(button);
          return li;
        }

        // Builds the Size options from every size seen so far. A range ("45-50 cm") is
        // always an option. A single size or a chain set is folded into every range it
        // fits in, boundaries included, so it matches there and leaves the list. A value
        // that fits in no range stays as its own option. Recomputed after each batch,
        // because a range can arrive later than the sizes it covers.
        rebuildSizeOptions() {
          var self = this;
          var filter = this.filtersEl ? this.filtersEl.querySelector('[data-bys-filter="size"]') : null;
          var listEl = filter ? filter.querySelector('.bys-filter__panel') : null;
          if (!listEl) return;

          var parsed = {};
          var ranges = [];
          this.sizeValues.forEach(function (value) {
            parsed[value] = parseSize(value);
            if (parsed[value] && parsed[value].kind === 'range') ranges.push(value);
          });

          var groups = {};
          var options = [];
          this.sizeValues.forEach(function (value) {
            var size = parsed[value];
            var homes = [];
            if (size && size.kind === 'range') {
              // Two spellings of the same range ("50-55 cm", "50–55 cm") become one option.
              for (var r = 0; r < ranges.length; r += 1) {
                if (parsed[ranges[r]].min === size.min && parsed[ranges[r]].max === size.max) {
                  if (ranges[r] !== value) homes.push(ranges[r]);
                  break;
                }
              }
            } else if (size) {
              ranges.forEach(function (range) {
                if (size.min >= parsed[range].min && size.max <= parsed[range].max) homes.push(range);
              });
            }
            if (!homes.length) {
              homes = [value];
              options.push(value);
            }
            homes.forEach(function (home) {
              groups[home] = groups[home] || [];
              if (groups[home].indexOf(value) === -1) groups[home].push(value);
            });
          });

          // The chosen option stays available even if a later batch folded it into a range.
          var selected = this.filters.size;
          if (selected && options.indexOf(selected) === -1 && this.sizeValues.indexOf(selected) !== -1) {
            options.push(selected);
            groups[selected] = [selected];
          }

          options.sort(function (a, b) {
            var first = (parsed[a] ? parsed[a].min : 0) - (parsed[b] ? parsed[b].min : 0);
            return first || self.sizeValues.indexOf(a) - self.sizeValues.indexOf(b);
          });
          this.sizeGroups = groups;

          Array.prototype.forEach.call(listEl.querySelectorAll('li'), function (li) {
            var button = li.querySelector('.bys-filter__option');
            if (button && button.dataset.bysValue) li.remove();
          });
          options.forEach(function (value) {
            listEl.appendChild(self.filterOption(value, value, value === selected));
          });
          filter.hidden = options.length === 0;
        }

        // True when one of the panel's sizes belongs to the chosen size option.
        sizeMatches(sizes, selected) {
          var members = this.sizeGroups[selected] || [selected];
          var list = sizes || '';
          for (var i = 0; i < members.length; i += 1) {
            if (list.indexOf('|' + members[i] + '|') !== -1) return true;
          }
          return false;
        }

        // The size chip to select for a size option: the exact value first, otherwise
        // the first available size that is folded into that range.
        chipForSize(panel, selected) {
          var members = this.sizeGroups[selected] || [selected];
          var chips = panel.querySelectorAll('[data-bys-chip]');
          var fallback = null;
          for (var i = 0; i < chips.length; i += 1) {
            if (chips[i].disabled) continue;
            var text = chips[i].textContent.trim();
            if (text === selected) return chips[i];
            if (!fallback && members.indexOf(text) !== -1) fallback = chips[i];
          }
          return fallback;
        }

        // Adds the cards of a batch to the grid: products already shown are skipped,
        // panels of a known colour group join that card, new cards keep source order.
        mergeCards(batch, job) {
          var self = this;
          var base = (job.source.index || 0) * 1000000 + job.page * 1000;
          var only = Array.isArray(job.source.only) && job.source.only.length ? job.source.only : null;
          var touched = [];
          Array.prototype.forEach.call(batch.querySelectorAll('bys-card'), function (incoming, position) {
            var group = incoming.dataset.bysGroup || '';
            var fresh = [];
            Array.prototype.forEach.call(incoming.querySelectorAll('[data-bys-panel]'), function (panel) {
              var id = panel.dataset.bysProduct;
              if (!id || self.panelFor(id)) return;
              if (only && !only.some(function (handle) { return (panel.dataset.bysTypes || '').indexOf('|' + handle + '|') !== -1; })) return;
              fresh.push(document.importNode(panel, true));
            });
            if (!fresh.length) return;
            var card = group ? self.cardFor(group) : null;
            if (!card) {
              card = document.createElement('bys-card');
              card.className = 'bys-card';
              card.dataset.bysGroup = group;
              card.bysSeq = base + position;
              self.insertCard(card);
              if (group) self.cardIndex[group] = card;
            }
            fresh.forEach(function (panel) {
              self.preparePanel(panel);
              card.appendChild(panel);
              self.panelIndex[panel.dataset.bysProduct] = panel;
            });
            if (touched.indexOf(card) === -1) touched.push(card);
          });
          touched.forEach(function (card) {
            if (typeof card.syncGroup === 'function') card.syncGroup(self.productId, self.currentColor);
          });
        }

        insertCard(card) {
          var children = this.grid.children;
          for (var i = 0; i < children.length; i += 1) {
            if (typeof children[i].bysSeq === 'number' && children[i].bysSeq > card.bysSeq) {
              this.grid.insertBefore(card, children[i]);
              return;
            }
          }
          this.grid.appendChild(card);
        }

        // Fills in what the batch can't render: the fixed state of the product being viewed,
        // labels and the icon from the block settings.
        preparePanel(panel) {
          var fixed = panel.dataset.bysProduct === this.productId;
          var addBtn = panel.querySelector('[data-bys-add]');
          var inStack = panel.querySelector('[data-bys-in-stack]');
          if (fixed) {
            panel.setAttribute('data-bys-fixed', '');
            panel.classList.add('bys-card__panel--fixed', 'bys-card__panel--active');
            if (addBtn) addBtn.remove();
            if (inStack) inStack.textContent = this.labels.inStack;
          } else {
            if (addBtn) addBtn.setAttribute('aria-label', this.labels.add);
            if (inStack) inStack.remove();
          }
          var customIcon = this.zoomIconTpl && this.zoomIconTpl.content ? this.zoomIconTpl.content.firstElementChild : null;
          if (customIcon) {
            var zoomBtn = panel.querySelector('[data-bys-zoom]');
            var icon = zoomBtn ? zoomBtn.querySelector('.bys-icon--zoom') : null;
            if (zoomBtn && icon) zoomBtn.replaceChild(document.importNode(customIcon, true), icon);
          }
        }

        cardFor(group) {
          var indexed = this.cardIndex[group];
          if (indexed && indexed.isConnected) return indexed;
          var cards = this.grid.querySelectorAll('bys-card');
          for (var i = 0; i < cards.length; i += 1) {
            if (cards[i].dataset.bysGroup === group) {
              this.cardIndex[group] = cards[i];
              return cards[i];
            }
          }
          return null;
        }

        warmImages() {
          var images = this.grid.querySelectorAll('bys-card:not([hidden]) [data-bys-panel]:not([hidden]) .bys-card__shot--active img');
          for (var i = 0; i < images.length && i < 8; i += 1) {
            images[i].setAttribute('loading', 'eager');
          }
        }

        panelFor(productId) {
          var id = String(productId);
          var indexed = this.panelIndex[id];
          if (indexed && indexed.isConnected) return indexed;
          var panels = this.grid.querySelectorAll('[data-bys-panel]');
          for (var i = 0; i < panels.length; i += 1) {
            if (panels[i].dataset.bysProduct === id) {
              this.panelIndex[id] = panels[i];
              return panels[i];
            }
          }
          return null;
        }

        dataFor(item, index) {
          if (index === 0) {
            var v = item.variantId ? this.mainData.variants[item.variantId] : null;
            return {
              price: v ? v.price : null,
              priceHtml: v ? v.priceHtml : '',
              saleHtml: v ? v.saleHtml : '',
              title: this.mainData.title || '',
              image: (v && v.image) || this.mainData.image || ''
            };
          }
          var panel = this.panelFor(item.productId);
          if (!panel) return item.display || null;
          var chip = null;
          if (item.variantId) {
            var chips = panel.querySelectorAll('[data-bys-chip]');
            for (var i = 0; i < chips.length; i += 1) {
              if (chips[i].dataset.bysVariant === String(item.variantId)) chip = chips[i];
            }
          }
          var source = chip || panel;
          return {
            price: parseInt(source.dataset.bysPrice, 10),
            priceHtml: source.dataset.bysPriceHtml || '',
            saleHtml: source.dataset.bysSaleHtml || '',
            title: panel.dataset.bysTitle || '',
            image: this.slotImage(panel)
          };
        }

        slotImage(panel) {
          var id = panel.dataset.bysProduct;
          if (!this.slotImages[id]) {
            var img = panel.querySelector('[data-bys-shot] img');
            var loaded = img && img.complete && img.naturalWidth > 0 ? img.currentSrc || img.src : '';
            this.slotImages[id] = loaded || panel.dataset.bysImage || '';
          }
          return this.slotImages[id];
        }

        fullPriceIndex(datas) {
          var best = -1;
          var bestPrice = -1;
          datas.forEach(function (d, i) {
            if (!d || typeof d.price !== 'number' || isNaN(d.price)) return;
            if (d.price > bestPrice) {
              bestPrice = d.price;
              best = i;
            }
          });
          return best === -1 ? 0 : best;
        }

        priceMarkup(data, isFull) {
          if (!data) return '';
          if (data.price === null || data.price === undefined || isNaN(data.price)) {
            return '<span class="bys-price__hint">' + escapeHtml(this.labels.selectSize) + '</span>';
          }
          if (isFull) return '<span class="bys-price__now">' + data.priceHtml + '</span>';
          return '<span class="bys-price__now">' + data.saleHtml + '</span><s class="bys-price__was">' + data.priceHtml + '</s>';
        }

        renderAll() {
          var self = this;
          var datas = this.items.map(function (item, i) { return self.dataFor(item, i); });
          var fullIndex = this.fullPriceIndex(datas);

          var active = document.activeElement;
          var focusSlot = active && active.closest ? active.closest('.bys-slot') : null;
          var focusRoot = null;
          if (focusSlot && this.traySlotsEl && this.traySlotsEl.contains(focusSlot)) focusRoot = this.traySlotsEl;
          else if (focusSlot && this.slotsEl.contains(focusSlot)) focusRoot = this.slotsEl;
          var focusIndex = focusSlot ? focusSlot.dataset.bysIndex : null;
          var focusWasRemove = !!(active && active.closest && active.closest('[data-bys-slot-remove]'));

          this.renderExpanded();
          this.renderMain(datas[0], fullIndex === 0);
          this.renderSlots(this.slotsEl, datas, fullIndex, false);
          if (this.traySlotsEl) this.renderSlots(this.traySlotsEl, datas, fullIndex, true);
          this.refreshStates();
          this.updateCounter();
          this.highlightReplace();

          if (focusRoot) {
            var slot = focusRoot.querySelector('.bys-slot[data-bys-index="' + focusIndex + '"]') || focusRoot.lastElementChild;
            var target = slot && ((focusWasRemove && slot.querySelector('[data-bys-slot-remove]')) || slot.querySelector('[data-bys-slot-open]'));
            if (target) target.focus();
          }
          if (this.isOpen && !(this.zoom && this.zoom.isOpen)) {
            var current = document.activeElement;
            var trapFocusEl = current && this.modal.contains(current) ? current : this.panel;
            trap(this.modal, trapFocusEl);
            if (document.activeElement === trapFocusEl) {
              trapFocusEl.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
            }
          }

          if (this.productRoot) this.productRoot.classList.toggle('bys-has-extras', this.items.length > 1);

          this.dispatchEvent(new CustomEvent('on:bys:change', { bubbles: true, detail: { items: this.items.slice() } }));
        }

        renderExpanded() {
          var self = this;
          if (this.startBtn) this.startBtn.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
          if (this.expanded) this.stage.removeAttribute('inert');
          else this.stage.setAttribute('inert', '');
          if (this.classList.contains('bys--expanded') === this.expanded) return;
          if (!this.animateToggle) {
            this.classList.toggle('bys--expanded', this.expanded);
            return;
          }
          if (this.expanded) {
            nextFrame(function () {
              if (!self.expanded) return;
              self.classList.add('bys--animated');
              self.classList.add('bys--expanded');
            });
          } else {
            this.classList.add('bys--animated');
            this.classList.remove('bys--expanded');
          }
        }

        renderMain(data, isFull) {
          if (!this.mainPriceEl || !data) return;
          this.mainPriceEl.innerHTML = this.priceMarkup(data, isFull);
          var img = this.mainSlot ? this.mainSlot.querySelector('img') : null;
          if (img && data.image && img.getAttribute('src') !== data.image) {
            img.removeAttribute('srcset');
            img.src = data.image;
          }
          var fixedPanel = this.grid.querySelector('[data-bys-panel][data-bys-fixed]');
          var card = fixedPanel ? fixedPanel.closest('bys-card') : null;
          if (fixedPanel && card && this.items[0].variantId) card.setVariant(fixedPanel, this.items[0].variantId, true);
        }

        renderSlots(container, datas, fullIndex, isTray) {
          var self = this;
          var start = isTray ? 0 : 1;
          Array.prototype.slice.call(container.children).forEach(function (child) {
            if (!isTray && child.hasAttribute('data-bys-main-slot')) return;
            child.remove();
          });

          for (var i = start; i < this.total; i += 1) {
            var item = this.items[i];
            var slot;
            if (item && datas[i]) {
              slot = this.templates.filled.content.firstElementChild.cloneNode(true);
              var img = slot.querySelector('[data-bys-img]');
              if (img) {
                if (datas[i].image) img.src = datas[i].image;
                else img.removeAttribute('src');
                img.alt = '';
              }
              var name = slot.querySelector('[data-bys-name]');
              if (name) name.textContent = datas[i].title;
              if (i > 0) {
                var openLabel = slot.querySelector('[data-bys-slot-open]');
                if (openLabel && this.labels.replacePiece) openLabel.setAttribute('aria-label', this.labels.replacePiece + ': ' + datas[i].title);
                var removeLabel = slot.querySelector('[data-bys-slot-remove]');
                if (removeLabel && this.labels.removePiece) removeLabel.setAttribute('aria-label', this.labels.removePiece + ': ' + datas[i].title);
              }
              var price = slot.querySelector('[data-bys-price]');
              if (price) price.innerHTML = this.priceMarkup(datas[i], i === fullIndex);
              if (i === 0) {
                var remove = slot.querySelector('[data-bys-slot-remove]');
                if (remove) remove.remove();
                var open = slot.querySelector('[data-bys-slot-open]');
                if (open) {
                  open.setAttribute('aria-disabled', 'true');
                  open.removeAttribute('aria-label');
                }
                slot.classList.add('bys-slot--main');
              }
            } else {
              slot = this.templates.empty.content.firstElementChild.cloneNode(true);
              if (isTray) {
                var rte = slot.querySelector('[data-bys-empty-rte]');
                var eName = slot.querySelector('[data-bys-empty-name]');
                var ePrice = slot.querySelector('[data-bys-empty-price]');
                if (rte) rte.hidden = true;
                if (eName) {
                  eName.hidden = false;
                  eName.textContent = fill(self.labels.emptyTitle, { n: i + 1 });
                }
                if (ePrice) {
                  ePrice.hidden = false;
                  ePrice.innerHTML = escapeHtml(fill(self.labels.emptyText, { percent: '\u0000' }))
                    .replace('\u0000', '<strong>' + escapeHtml(self.percent + '%') + '</strong>');
                }
              }
              if (this.items.length >= this.total) slot.classList.add('bys-slot--full');
            }
            slot.dataset.bysIndex = String(i);
            if (isTray) slot.classList.add('bys-slot--tray');
            container.appendChild(slot);
          }
        }

        refreshStates() {
          var self = this;
          this.grid.querySelectorAll('[data-bys-panel]').forEach(function (panel) {
            var fixed = panel.hasAttribute('data-bys-fixed');
            var item = null;
            for (var i = 1; i < self.items.length; i += 1) {
              if (self.items[i].productId === panel.dataset.bysProduct) item = self.items[i];
            }
            var active = fixed || !!item;
            panel.classList.toggle('bys-card__panel--active', active);
            var btn = panel.querySelector('[data-bys-add]');
            if (btn) {
              btn.setAttribute('aria-pressed', active ? 'true' : 'false');
              btn.setAttribute('aria-label', active ? self.labels.remove : self.labels.add);
            }
            if (item && item.variantId && item.variantId !== panel.dataset.bysVariant) {
              var card = panel.closest('bys-card');
              if (card) card.setVariant(panel, item.variantId, true);
            }
          });
        }

        updateCounter() {
          if (!this.counterEl) return;
          this.counterEl.textContent = fill(this.labels.tray, { count: this.items.length, total: this.total });
        }

        highlightReplace() {
          var self = this;
          var roots = [this.slotsEl, this.traySlotsEl];
          roots.forEach(function (root) {
            if (!root) return;
            root.querySelectorAll('.bys-slot').forEach(function (slot) {
              var armed = self.replaceIndex !== null && slot.dataset.bysIndex === String(self.replaceIndex);
              slot.classList.toggle('bys-slot--target', armed);
              var btn = slot.querySelector('[data-bys-slot-open]');
              if (btn && slot.classList.contains('bys-slot--filled')) btn.setAttribute('aria-pressed', armed ? 'true' : 'false');
            });
          });
        }

        setExpanded(expanded) {
          var self = this;
          this.expanded = expanded;
          this.animateToggle = true;
          window.clearTimeout(this.collapseTimer);
          if (!expanded) {
            this.items = [this.items[0]];
            this.replaceIndex = null;
            this.pendingItems = null;
            this.persist();
            this.renderExpanded();
            this.collapseTimer = window.setTimeout(function () {
              if (!self.expanded) self.renderAll();
            }, REDUCED_MOTION.matches ? 0 : 450);
            return;
          }
          this.persist();
          this.renderAll();
        }

        toggle(productId, variantId) {
          productId = String(productId);
          if (productId === this.productId) return;
          var index = -1;
          for (var i = 1; i < this.items.length; i += 1) {
            if (this.items[i].productId === productId) index = i;
          }
          if (index > 0) {
            this.items.splice(index, 1);
            this.forgetPending(productId);
            this.replaceIndex = null;
          } else if (this.replaceIndex !== null && this.replaceIndex > 0 && this.items[this.replaceIndex]) {
            this.forgetPending(this.items[this.replaceIndex].productId);
            this.items[this.replaceIndex] = { productId: productId, variantId: variantId ? String(variantId) : null };
            this.replaceIndex = null;
          } else if (this.items.length < this.total) {
            this.items.push({ productId: productId, variantId: variantId ? String(variantId) : null });
          } else {
            this.flashFull();
            return;
          }
          this.persist();
          this.renderAll();
        }

        removeAt(index) {
          if (index < 1 || !this.items[index]) return;
          this.forgetPending(this.items[index].productId);
          this.items.splice(index, 1);
          if (this.replaceIndex !== null) {
            if (index === this.replaceIndex) this.replaceIndex = null;
            else if (index < this.replaceIndex) this.replaceIndex -= 1;
          }
          this.persist();
          this.renderAll();
        }

        flashFull() {
          if (!this.counterEl) return;
          this.counterEl.classList.add('bys-tray__counter--flash');
          var el = this.counterEl;
          window.setTimeout(function () { el.classList.remove('bys-tray__counter--flash'); }, 600);
          if (this.tray && !DESKTOP.matches) this.setTrayOpen(true);
        }

        setTrayOpen(open) {
          if (!this.tray) return;
          this.tray.classList.toggle('bys-tray--open', open);
          this.syncTrayMode();
        }

        syncTrayMode() {
          if (!this.trayToggle || !this.tray) return;
          if (DESKTOP.matches) {
            this.trayToggle.tabIndex = -1;
            this.trayToggle.setAttribute('aria-expanded', 'true');
          } else {
            this.trayToggle.removeAttribute('tabindex');
            this.trayToggle.setAttribute('aria-expanded', this.tray.classList.contains('bys-tray--open') ? 'true' : 'false');
          }
        }

        open(opener, replaceIndex) {
          this.opener = opener || null;
          var openerSlot = opener && opener.closest ? opener.closest('.bys-slot') : null;
          this.openerIndex = openerSlot && this.slotsEl.contains(openerSlot) ? openerSlot.dataset.bysIndex : null;
          this.replaceIndex = typeof replaceIndex === 'number' ? replaceIndex : null;
          this.highlightReplace();
          if (this.gridState !== 'loaded') this.loadGrid();
          if (this.isOpen) return;
          this.isOpen = true;
          this.modal.hidden = false;
          this.modal.setAttribute('open', '');
          this.markSwatchRows();
          document.addEventListener('keydown', this.onKeydown);
          var self = this;
          nextFrame(function () {
            self.modal.classList.add('is-open');
            self.fitFilters();
            trap(self.modal, self.panel);
            if (self.gridState === 'ready') self.pump();
          });
        }

        close() {
          if (!this.isOpen) return;
          this.isOpen = false;
          this.replaceIndex = null;
          this.highlightReplace();
          this.closeFilterPanels();
          document.removeEventListener('keydown', this.onKeydown);
          this.modal.removeAttribute('open');
          this.modal.classList.remove('is-open');
          var self = this;
          afterTransition(this.panel, function () {
            self.modal.hidden = true;
            var back = self.opener && self.opener.isConnected ? self.opener : null;
            if (!back && self.openerIndex !== null) {
              back = self.slotsEl.querySelector('.bys-slot[data-bys-index="' + self.openerIndex + '"] [data-bys-slot-open]');
            }
            untrap(back || self.collapseBtn || self.startBtn);
          }, 450);
        }

        onKeydown(evt) {
          if (evt.key === 'Escape') {
            if (this.zoom && this.zoom.isOpen) {
              evt.preventDefault();
              this.zoom.close();
              return;
            }
            var openFilter = this.openFilter();
            if (openFilter) {
              evt.preventDefault();
              this.closeFilterPanels();
              var toggle = openFilter.querySelector('.bys-filter__toggle');
              if (toggle) toggle.focus();
              return;
            }
            evt.preventDefault();
            this.close();
            return;
          }

          if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
            var filter = this.openFilter();
            if (!filter) return;
            var options = Array.prototype.slice.call(filter.querySelectorAll('.bys-filter__option'));
            if (!options.length) return;
            evt.preventDefault();
            var index = options.indexOf(document.activeElement);
            var next = evt.key === 'ArrowDown' ? index + 1 : index - 1;
            if (next < 0) next = options.length - 1;
            if (next >= options.length) next = 0;
            options[next].focus();
          }
        }

        onBlockClick(evt) {
          var target = evt.target;
          if (target.closest('[data-bys-collapse]')) {
            evt.preventDefault();
            this.setExpanded(false);
            if (this.startBtn) this.startBtn.focus();
            return;
          }
          if (target.closest('[data-bys-start]') || target.closest('[data-bys-row]')) {
            evt.preventDefault();
            if (!this.expanded) {
              this.setExpanded(true);
              var collapseBtn = this.collapseBtn;
              nextFrame(function () {
                if (collapseBtn) collapseBtn.focus();
              });
            } else {
              this.open(this.collapseBtn || this.rowEl, null);
            }
            return;
          }
          var removeBtn = target.closest('[data-bys-slot-remove]');
          if (removeBtn && this.slotsEl.contains(removeBtn)) {
            evt.preventDefault();
            var slot = removeBtn.closest('.bys-slot');
            this.removeAt(parseInt(slot.dataset.bysIndex, 10));
            return;
          }
          var openBtn = target.closest('[data-bys-slot-open]');
          if (openBtn && this.slotsEl.contains(openBtn)) {
            evt.preventDefault();
            var s = openBtn.closest('.bys-slot');
            var index = parseInt(s.dataset.bysIndex, 10);
            this.open(openBtn, this.items[index] ? index : null);
          }
        }

        onModalClick(evt) {
          var target = evt.target;

          if (target.closest('[data-bys-close]') || target.closest('[data-bys-done]')) {
            evt.preventDefault();
            this.close();
            return;
          }

          if (target.closest('[data-bys-retry]')) {
            evt.preventDefault();
            this.loadGrid();
            return;
          }

          var filterToggle = target.closest('.bys-filter__toggle');
          if (filterToggle) {
            evt.preventDefault();
            this.toggleFilter(filterToggle.closest('[data-bys-filter]'));
            return;
          }

          var option = target.closest('.bys-filter__option');
          if (option) {
            evt.preventDefault();
            this.selectOption(option.closest('[data-bys-filter]'), option);
            return;
          }

          if (target.closest('[data-bys-reset]')) {
            evt.preventDefault();
            this.resetFilters();
            return;
          }

          if (!target.closest('[data-bys-filter]')) this.closeFilterPanels();

          if (target.closest('[data-bys-tray-toggle]')) {
            evt.preventDefault();
            if (!DESKTOP.matches) this.setTrayOpen(!this.tray.classList.contains('bys-tray--open'));
            return;
          }

          var removeBtn = target.closest('[data-bys-slot-remove]');
          if (removeBtn && this.traySlotsEl && this.traySlotsEl.contains(removeBtn)) {
            evt.preventDefault();
            this.removeAt(parseInt(removeBtn.closest('.bys-slot').dataset.bysIndex, 10));
            return;
          }

          var openBtn = target.closest('[data-bys-slot-open]');
          if (openBtn && this.traySlotsEl && this.traySlotsEl.contains(openBtn)) {
            evt.preventDefault();
            var index = parseInt(openBtn.closest('.bys-slot').dataset.bysIndex, 10);
            if (index === 0) return;
            this.replaceIndex = this.items[index] && this.replaceIndex !== index ? index : null;
            this.highlightReplace();
          }
        }

        onToggle(evt) {
          var d = evt.detail || {};
          this.toggle(d.productId, d.variantId);
        }

        onVariantPick(evt) {
          var d = evt.detail || {};
          for (var i = 1; i < this.items.length; i += 1) {
            if (this.items[i].productId === String(d.productId)) {
              this.items[i].variantId = d.variantId ? String(d.variantId) : null;
              this.persist();
              this.renderAll();
              return;
            }
          }
        }

        onSwitch() {
          this.refreshStates();
        }

        onZoom(evt) {
          if (!this.zoom || typeof this.zoom.open !== 'function') return;
          var d = evt.detail || {};
          this.zoom.open(d.title, d.images, d.index, d.opener);
        }

        onZoomClosed() {
          if (this.isOpen) trap(this.modal, document.activeElement || this.panel);
        }

        onVariantChange(evt) {
          var d = evt.detail || {};
          if (this.productRoot && !this.productRoot.contains(evt.target)) return;
          this.items[0].variantId = d.variant && d.variant.id ? String(d.variant.id) : null;
          this.renderAll();
        }

        onResize() {
          this.fitFilters();
          this.markSwatchRows();
          if (this.gridState === 'ready') this.pump();
        }

        openFilter() {
          var toggle = this.modal.querySelector('.bys-filter__toggle[aria-expanded="true"]');
          return toggle ? toggle.closest('[data-bys-filter]') : null;
        }

        closeFilterPanels() {
          this.modal.querySelectorAll('[data-bys-filter]').forEach(function (filter) {
            var toggle = filter.querySelector('.bys-filter__toggle');
            var panel = filter.querySelector('.bys-filter__panel');
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
            if (panel) panel.hidden = true;
          });
        }

        toggleFilter(filter) {
          if (!filter) return;
          var toggle = filter.querySelector('.bys-filter__toggle');
          var panel = filter.querySelector('.bys-filter__panel');
          var willOpen = toggle.getAttribute('aria-expanded') !== 'true';
          this.closeFilterPanels();
          if (willOpen) {
            toggle.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
            var selected = panel.querySelector('.bys-filter__option--active') || panel.querySelector('.bys-filter__option');
            if (selected) selected.focus();
          }
        }

        selectOption(filter, option) {
          if (!filter || !option) return;
          var name = filter.dataset.bysFilter;
          var value = option.dataset.bysValue || '';
          this.filters[name] = value;
          filter.querySelectorAll('.bys-filter__option').forEach(function (o) {
            var active = o === option;
            o.classList.toggle('bys-filter__option--active', active);
            o.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          var valueEl = filter.querySelector('[data-bys-filter-value]');
          if (valueEl) valueEl.textContent = option.textContent.trim();
          this.closeFilterPanels();
          var toggle = filter.querySelector('.bys-filter__toggle');
          if (toggle) toggle.focus();
          if (name === 'size') this.rebuildSizeOptions();
          this.applyFilters();
        }

        resetFilters() {
          var self = this;
          this.modal.querySelectorAll('[data-bys-filter]').forEach(function (filter) {
            var first = filter.querySelector('.bys-filter__option');
            if (first) self.selectOption(filter, first);
          });
          this.applyFilters();
        }

        // keepScroll: true while batches arrive, so the grid doesn't jump back to the top.
        applyFilters(keepScroll) {
          var self = this;
          var f = this.filters;
          var visible = 0;
          this.grid.querySelectorAll('bys-card').forEach(function (card) {
            var panels = card.panels ? card.panels() : [];
            var active = card.activePanel ? card.activePanel() : null;
            if (!active && panels.length) {
              card.showPanel(panels[0].dataset.bysProduct, true);
              active = panels[0];
            }
            if (!active) {
              card.hidden = true;
              return;
            }
            if (f.color && active.dataset.bysColor !== f.color) {
              var match = null;
              panels.forEach(function (p) {
                if (!match && p.dataset.bysColor === f.color) match = p;
              });
              if (!match) {
                card.hidden = true;
                return;
              }
              card.showPanel(match.dataset.bysProduct, true);
              active = match;
            }
            // Type is checked on the visible panel, so if a sibling colour matches the
            // chosen type instead (e.g. "Silver chains" on a gold/silver pair), show that one.
            if (f.type && (active.dataset.bysTypes || '').indexOf('|' + f.type + '|') === -1) {
              var typeMatch = null;
              panels.forEach(function (p) {
                if (typeMatch) return;
                if ((p.dataset.bysTypes || '').indexOf('|' + f.type + '|') === -1) return;
                if (f.color && p.dataset.bysColor !== f.color) return;
                typeMatch = p;
              });
              if (typeMatch) {
                card.showPanel(typeMatch.dataset.bysProduct, true);
                active = typeMatch;
              }
            }
            var ok = true;
            if (f.type && (active.dataset.bysTypes || '').indexOf('|' + f.type + '|') === -1) ok = false;
            if (f.size && !self.sizeMatches(active.dataset.bysSizes, f.size)) ok = false;
            card.hidden = !ok;
            if (!ok) return;
            visible += 1;
            if (f.size && !active.hasAttribute('data-bys-fixed')) {
              var chip = self.chipForSize(active, f.size);
              if (chip) card.setVariant(active, chip.dataset.bysVariant, true);
            }
          });
          if (this.emptyMsg) this.emptyMsg.hidden = visible > 0 || this.gridState !== 'loaded';
          if (this.resetBtn) this.resetBtn.hidden = !(f.type || f.color || f.size);
          this.refreshStates();
          this.fitFilters();
          this.markSwatchRows();
          if (!keepScroll && this.grid.parentNode) this.grid.parentNode.scrollTop = 0;
          if (this.gridState === 'ready') this.pump();
        }

        fitFilters() {
          if (!this.filtersEl || this.modal.hidden) return;
          this.filtersEl.classList.remove('bys-filters--wrap');
          this.filtersEl.style.fontSize = '';
          if (DESKTOP.matches) return;
          var size = 16;
          var guard = 0;
          while (this.filtersEl.scrollWidth > this.filtersEl.clientWidth + 1 && size > 12 && guard < 20) {
            size -= 0.5;
            guard += 1;
            this.filtersEl.style.fontSize = size + 'px';
          }
          if (this.filtersEl.scrollWidth > this.filtersEl.clientWidth + 1) this.filtersEl.classList.add('bys-filters--wrap');
        }

        onSubmit(evt) {
          var form = evt.target;
          if (!(form instanceof HTMLFormElement) || !form.isConnected || (form !== this.form && form !== this.stickyForm)) return;
          var extras = this.items.slice(1).filter(function (i) { return i.variantId; });
          if (!extras.length) return;

          var mainForm = this.form || form;
          var idInput = mainForm.querySelector('[name="id"]');
          var mainId = idInput && idInput.value ? parseInt(idInput.value, 10) : NaN;
          if (!mainId) return;

          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation();

          var isSticky = form !== mainForm;
          var mainPf = mainForm.closest('product-form');
          var errorPf = form.closest('product-form, sticky-atc') || mainPf;
          var submitBtn = (errorPf || form).querySelector('[name="add"]');
          if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') return;

          if (errorPf && typeof errorPf.setErrorMsgState === 'function') errorPf.setErrorMsgState();
          if (mainPf && typeof mainPf.validate === 'function' && !mainPf.validate()) {
            if (isSticky && window.theme && typeof window.theme.scrollToRevealElement === 'function') window.theme.scrollToRevealElement(mainPf);
            return;
          }
          if (typeof mainForm.reportValidity === 'function' && !mainForm.reportValidity()) {
            if (isSticky && window.theme && typeof window.theme.scrollToRevealElement === 'function') {
              var invalid = Array.prototype.find.call(mainForm.elements, function (el) { return !el.checkValidity(); });
              if (invalid) window.setTimeout(function () { window.theme.scrollToRevealElement(invalid); }, 100);
            }
            return;
          }

          var formData = new FormData(mainForm);
          var main = { id: mainId, quantity: parseInt(formData.get('quantity'), 10) || 1 };
          formData.forEach(function (value, key) {
            var prop = /^properties\[(.+)\]$/.exec(key);
            if (prop) {
              main.properties = main.properties || {};
              main.properties[prop[1]] = value;
            } else if (key === 'selling_plan' && value) {
              main.selling_plan = value;
            }
          });
          var items = [main].concat(extras.map(function (e) { return { id: parseInt(e.variantId, 10), quantity: 1 }; }));

          var sectionsToUpdate = ['page-header', 'cart-drawer']
            .map(function (sel) { return document.querySelector(sel); })
            .filter(function (el) { return el && el.dataset.sectionId; });
          var sectionIds = sectionsToUpdate.map(function (el) { return el.dataset.sectionId; });

          if (submitBtn) {
            submitBtn.setAttribute('aria-disabled', 'true');
            submitBtn.classList.add('is-loading');
          }

          var self = this;
          var theme = window.theme || {};
          var routes = theme.routes || {};
          var settings = theme.settings || {};
          var route = routes.cartAdd || '/cart/add.js';
          var target = errorPf || this;

          fetch(route, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items: items, sections: sectionIds.join(','), sections_url: window.location.pathname })
          })
            .then(function (response) {
              return response.json().then(function (data) { return { ok: response.ok, status: response.status, data: data }; });
            })
            .then(function (result) {
              var data = result.data || {};
              if (!result.ok) {
                var error = typeof data.description === 'string' ? data.description : data.message;
                if (data.errors && typeof data.errors === 'object') {
                  error = Object.keys(data.errors).map(function (k) { return [].concat(data.errors[k]).join(', '); }).join(' ');
                }
                var message = error || self.labels.addError;
                if (message && errorPf && typeof errorPf.setErrorMsgState === 'function') errorPf.setErrorMsgState(message);
                target.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: error } }));
                document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
                var handled = new Error(String(result.status));
                handled.handled = true;
                throw handled;
              }

              var after = settings.afterAddToCart;
              if (document.querySelector('.template-cart')) {
                var cartForm = document.querySelector('cart-form');
                if (cartForm && cartForm.enableAjaxUpdate && typeof cartForm.refresh === 'function') cartForm.refresh();
              } else if (after === 'page' || after === 'no-js') {
                window.setTimeout(function () { window.location.href = routes.cart || '/cart'; }, 300);
              } else {
                var allSections = data.sections && sectionsToUpdate.every(function (el) { return data.sections[el.dataset.sectionId]; });
                if (allSections) {
                  sectionsToUpdate.forEach(function (el) {
                    if (typeof el.updateFromCartChange === 'function') el.updateFromCartChange(data.sections[el.dataset.sectionId]);
                  });
                } else {
                  document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
                }

                if (after === 'notification') {
                  var tpl = document.getElementById('AddedNotification');
                  if (tpl && tpl.content && tpl.content.firstElementChild) {
                    var notification = tpl.content.firstElementChild.cloneNode(true);
                    notification.dataset.productTitle = data.items && data.items[0] ? data.items[0].product_title : (self.mainData.title || '');
                    (document.querySelector('.pageheader--sticky') || document.body).appendChild(notification);
                  }
                } else if (after === 'drawer') {
                  var drawer = document.querySelector('.js-cart-drawer');
                  if (drawer && typeof drawer.open === 'function') drawer.open(submitBtn);
                }
              }

              target.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: mainId } }));

              if (submitBtn) {
                submitBtn.classList.add('is-success');
                submitBtn.removeAttribute('aria-disabled');
                window.setTimeout(function () {
                  submitBtn.classList.remove('is-loading');
                  submitBtn.classList.remove('is-success');
                }, 2000);
              }

              self.items = [self.items[0]];
              self.replaceIndex = null;
              self.pendingItems = null;
              self.persist();
              self.renderAll();
            })
            .catch(function (error) {
              console.log(error);
              if (!error || !error.handled) {
                if (errorPf && typeof errorPf.setErrorMsgState === 'function' && self.labels.addError) errorPf.setErrorMsgState(self.labels.addError);
                target.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: self.labels.addError } }));
                document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
              }
              if (submitBtn) {
                submitBtn.classList.remove('is-loading');
                submitBtn.classList.remove('is-success');
                submitBtn.removeAttribute('aria-disabled');
              }
            });
        }
      }
    );
  }
})();
